import React, { useState, useEffect } from 'react';
import {
  Form,
  DatePicker,
  InputNumber,
  Select,
  Row,
  Col,
  Table,
  Tag,
  Empty,
  Spin,
  message,
  Statistic,
  Collapse,
  Typography,
  Divider,
  Tooltip,
  Card,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  PercentageOutlined,
  InfoCircleOutlined,
  CalculatorOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchEquipmentTypes, fetchReconciliationSummary } from './api';

const { Text, Title } = Typography;

// ── Styles ──────────────────────────────────────────────
const filterStyles = {
  filters: {
    background: '#ffffff',
    borderRadius: '8px',
    padding: '8px 12px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.08)',
    marginBottom: '12px',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'nowrap',
    border: '1px solid #e2e8f0',
    overflowX: 'auto',
  },
  filterDivider: {
    width: '1px',
    height: '24px',
    background: '#e2e8f0',
    flexShrink: 0,
  },
  timeSlicers: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    flexShrink: 0,
  },
  timeBtn: {
    padding: '5px 10px',
    border: '1px solid #e2e8f0',
    background: '#ffffff',
    color: '#4a5568',
    borderRadius: '5px',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  timeBtnActive: {
    background: '#3182ce',
    color: 'white',
    borderColor: '#3182ce',
  },
};

const formulaStyle = {
  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
  backgroundColor: '#f6f8fa',
  padding: '4px 8px',
  borderRadius: '4px',
  fontSize: '13px',
};

const formulaBlockStyle = {
  backgroundColor: '#f6f8fa',
  padding: '12px 16px',
  borderRadius: '6px',
  marginBottom: '12px',
  border: '1px solid #e8e8e8',
};

// ── Tooltip Content ──────────────────────────────────────
const tooltipContent = {
  correctMatches: (
    <div style={{ maxWidth: 260 }}>
      <Text strong>Correct Matches</Text><br />
      <Text style={{ fontFamily: 'monospace', fontSize: '12px', background: '#f5f5f5', padding: '4px 6px', borderRadius: '4px', display: 'inline-block', marginTop: 4, marginBottom: 4 }}>
        = (Correct Matches / Total Physical Entries) × 100
      </Text><br />
      <Text type="secondary" style={{ fontSize: '11px' }}>Entries where digital log timing matches physical log within buffer.</Text>
    </div>
  ),
  wrongTimings: (
    <div style={{ maxWidth: 260 }}>
      <Text strong>Wrong Timings</Text><br />
      <Text style={{ fontFamily: 'monospace', fontSize: '12px', background: '#f5f5f5', padding: '4px 6px', borderRadius: '4px', display: 'inline-block', marginTop: 4, marginBottom: 4 }}>
        = (Wrong Timings / Total Physical Entries) × 100
      </Text><br />
      <Text type="secondary" style={{ fontSize: '11px' }}>Entries digitally logged but outside acceptable buffer.</Text>
    </div>
  ),
  missingEntries: (
    <div style={{ maxWidth: 260 }}>
      <Text strong>Missing Entries</Text><br />
      <Text style={{ fontFamily: 'monospace', fontSize: '12px', background: '#f5f5f5', padding: '4px 6px', borderRadius: '4px', display: 'inline-block', marginTop: 4, marginBottom: 4 }}>
        = (Missing Entries / Total Physical Entries) × 100
      </Text><br />
      <Text type="secondary" style={{ fontSize: '11px' }}>Physical entries without a corresponding digital log.</Text>
    </div>
  ),
  digitalLoggingRate: (
    <div style={{ maxWidth: 260 }}>
      <Text strong>Digital Logging Rate</Text><br />
      <Text style={{ fontFamily: 'monospace', fontSize: '12px', background: '#f5f5f5', padding: '4px 6px', borderRadius: '4px', display: 'inline-block', marginTop: 4, marginBottom: 4 }}>
        = (Digitally Logged / Total Physical Entries) × 100
      </Text><br />
      <Text type="secondary" style={{ fontSize: '11px' }}>Percentage of physical entries that were digitally logged.</Text>
    </div>
  ),
  accuracy: (
    <div style={{ maxWidth: 260 }}>
      <Text strong>Accuracy</Text><br />
      <Text style={{ fontFamily: 'monospace', fontSize: '12px', background: '#f5f5f5', padding: '4px 6px', borderRadius: '4px', display: 'inline-block', marginTop: 4, marginBottom: 4 }}>
        = (Correct Matches / Total Physical Entries) × 100
      </Text><br />
      <Text type="secondary" style={{ fontSize: '11px' }}>Percentage of correctly matched entries within timing buffer.</Text>
    </div>
  ),
};

const ColumnHeaderWithTooltip = ({ title, tooltipKey }) => (
  <span>
    {title}{' '}
    <Tooltip
      title={tooltipContent[tooltipKey]}
      placement="top"
      overlayInnerStyle={{ backgroundColor: '#ffffff', color: '#000000', border: '1px solid #d9d9d9', boxShadow: '0 3px 8px rgba(0,0,0,0.15)' }}
      overlayStyle={{ maxWidth: 300 }}
    >
      <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'help' }} />
    </Tooltip>
  </span>
);

const QUICK_RANGES = ['1D', '1W', '1M', '3M', '6M'];

// ── Component ────────────────────────────────────────────
const EquipmentLogAccuracyDashboard = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [equipmentTypesLoading, setEquipmentTypesLoading] = useState(false);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [overallData, setOverallData] = useState(null);
  const [activeQuickRange, setActiveQuickRange] = useState(null);
  const [equipmentTypeFilter, setEquipmentTypeFilter] = useState('ALL');
  const [bufferTime, setBufferTime] = useState(15);

  useEffect(() => {
    loadEquipmentTypes();
  }, []);

  const loadEquipmentTypes = async () => {
    setEquipmentTypesLoading(true);
    try {
      const response = await fetchEquipmentTypes();
      if (response.success && Array.isArray(response.data)) {
        setEquipmentTypes(response.data.filter((type) => type !== 'ALL'));
      }
    } catch (error) {
      message.error('Failed to load equipment types');
    } finally {
      setEquipmentTypesLoading(false);
    }
  };

  const applyQuickRange = (range) => {
    const today = dayjs();
    let startDate, endDate;
    switch (range) {
      case '1D':
        startDate = today.subtract(1, 'day');
        endDate = today;
        break;
      case '1W':
        startDate = today.subtract(7, 'day');
        endDate = today;
        break;
      case '1M':
        startDate = today.subtract(1, 'month');
        endDate = today;
        break;
      case '3M':
        startDate = today.subtract(3, 'month');
        endDate = today;
        break;
      case '6M':
        startDate = today.subtract(6, 'month');
        endDate = today;
        break;
      default:
        break;
    }
    setActiveQuickRange(range);
    form.setFieldsValue({ startDate, endDate });
  };

  const handleApplyFilters = async () => {
    try {
      const values = await form.validateFields();
      const { startDate, endDate } = values;
      const fromDate = dayjs(startDate).format('YYYY-MM-DD');
      const toDate = dayjs(endDate).format('YYYY-MM-DD');

      setLoading(true);
      const response = await fetchReconciliationSummary({
        from_date: fromDate,
        to_date: toDate,
        buffer: bufferTime,
        equipment_type: equipmentTypeFilter || 'ALL',
      });

      if (response.success && Array.isArray(response.data)) {
        const overall = response.data.find((item) => item.equipment_type === 'Overall');
        const otherData = response.data.filter((item) => item.equipment_type !== 'Overall');
        const processedData = otherData.map((item, index) => ({
          ...item,
          key: index,
          ...calculatePercentages(item),
        }));
        if (overall) {
          setOverallData({ ...overall, ...calculatePercentages(overall) });
          processedData.push({ ...overall, key: 'overall', ...calculatePercentages(overall) });
        } else {
          setOverallData(null);
        }
        setTableData(processedData);
      } else {
        setTableData([]);
        setOverallData(null);
      }
    } catch (error) {
      if (error?.errorFields) return;
      message.error('Failed to fetch reconciliation data');
      setTableData([]);
      setOverallData(null);
    } finally {
      setLoading(false);
    }
  };

  const calculatePercentages = (item) => {
    const totalEntries = item.total_entries || 0;
    if (totalEntries === 0) {
      return { correctPercent: 0, wrongPercent: 0, missingPercent: 0, digitalLoggingRate: 0, accuracy: 0 };
    }
    return {
      correctPercent: ((item.match_found / totalEntries) * 100).toFixed(1),
      wrongPercent: ((item.wrong_timings / totalEntries) * 100).toFixed(1),
      missingPercent: ((item.missing / totalEntries) * 100).toFixed(1),
      digitalLoggingRate: ((item.digitally_logged / totalEntries) * 100).toFixed(1),
      accuracy: ((item.match_found / totalEntries) * 100).toFixed(1),
    };
  };

  const getAccuracyTagColor = (accuracy) => {
    const value = parseFloat(accuracy);
    if (value >= 90) return 'green';
    if (value >= 70) return 'orange';
    return 'red';
  };

  const handleReset = () => {
    form.resetFields();
    setTableData([]);
    setOverallData(null);
    setActiveQuickRange(null);
    setEquipmentTypeFilter('ALL');
    setBufferTime(15);
  };

  const columns = [
    { title: 'Equipment Type', dataIndex: 'equipment_type', key: 'equipment_type', width: 150 },
    { title: 'Total Physical Entries', dataIndex: 'total_entries', key: 'total_entries', width: 150, align: 'center' },
    { title: 'Digitally Logged', dataIndex: 'digitally_logged', key: 'digitally_logged', width: 130, align: 'center' },
    {
      title: <ColumnHeaderWithTooltip title="Correct Matches" tooltipKey="correctMatches" />,
      key: 'correct_matches', width: 160, align: 'center',
      render: (_, record) => <span>{record.match_found} ({record.correctPercent}%)</span>,
    },
    {
      title: <ColumnHeaderWithTooltip title="Wrong Timings" tooltipKey="wrongTimings" />,
      key: 'wrong_timings', width: 150, align: 'center',
      render: (_, record) => <span>{record.wrong_timings} ({record.wrongPercent}%)</span>,
    },
    {
      title: <ColumnHeaderWithTooltip title="Missing Entries" tooltipKey="missingEntries" />,
      key: 'missing_entries', width: 150, align: 'center',
      render: (_, record) => <span>{record.missing} ({record.missingPercent}%)</span>,
    },
    {
      title: <ColumnHeaderWithTooltip title="Digital Logging Rate (%)" tooltipKey="digitalLoggingRate" />,
      key: 'digital_logging_rate', width: 190, align: 'center',
      render: (_, record) => <span>{record.digitalLoggingRate}%</span>,
    },
    {
      title: <ColumnHeaderWithTooltip title="Accuracy (%)" tooltipKey="accuracy" />,
      key: 'accuracy', width: 140, align: 'center',
      render: (_, record) => <Tag color={getAccuracyTagColor(record.accuracy)}>{record.accuracy}%</Tag>,
    },
  ];

  return (
    <div style={{ padding: '16px', backgroundColor: '#f5f5f5', minHeight: '100vh', fontFamily: "'DM Sans', -apple-system, sans-serif" }}>

      {/* Page Header */}
      <div style={{
        background: '#ffffff', borderBottom: '1px solid #e2e8f0', padding: '12px 16px',
        marginBottom: '12px', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)', borderRadius: '8px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <h1 style={{ fontSize: '18px', fontWeight: '700', color: '#1a202c', letterSpacing: '-0.5px', margin: 0 }}>
          Equipment Log Accuracy Dashboard
        </h1>
        <span style={{ fontSize: '12px', color: '#4a5568' }}>
          Real-time monitoring of digital vs physical logbook compliance
        </span>
      </div>

      {/* ── Filter Bar ───────────────────────────────────── */}
      <div style={filterStyles.filters}>

        {/* Quick Time Range Buttons */}
        <div style={filterStyles.timeSlicers}>
          {QUICK_RANGES.map((range) => (
            <button
              key={range}
              style={{
                ...filterStyles.timeBtn,
                ...(activeQuickRange === range ? filterStyles.timeBtnActive : {})
              }}
              onClick={() => applyQuickRange(range)}
            >
              {range}
            </button>
          ))}
        </div>

        <div style={filterStyles.filterDivider} />

        {/* Date Pickers */}
        <Form form={form} layout="inline" style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: 0 }}>
          <Form.Item name="startDate" style={{ margin: 0 }} rules={[{ required: true, message: 'Start date required' }]}>
            <DatePicker
              format="YYYY-MM-DD"
              placeholder="Start date"
              size="small"
              style={{ width: '120px' }}
              onChange={() => setActiveQuickRange(null)}
            />
          </Form.Item>
          <span style={{ color: '#a0aec0', fontSize: '12px' }}>→</span>
          <Form.Item name="endDate" style={{ margin: 0 }} rules={[{ required: true, message: 'End date required' }]}>
            <DatePicker
              format="YYYY-MM-DD"
              placeholder="End date"
              size="small"
              style={{ width: '120px' }}
              onChange={() => setActiveQuickRange(null)}
            />
          </Form.Item>
        </Form>

        <div style={filterStyles.filterDivider} />

        {/* Buffer Time */}
        <InputNumber
          size="small"
          min={0}
          value={bufferTime}
          onChange={(val) => setBufferTime(val)}
          placeholder="Buffer (min)"
          style={{ width: '105px' }}
        />

        <div style={filterStyles.filterDivider} />

        {/* Equipment Type */}
        <Select
          size="small"
          value={equipmentTypeFilter}
          onChange={(val) => setEquipmentTypeFilter(val || 'ALL')}
          loading={equipmentTypesLoading}
          style={{ minWidth: '140px' }}
          allowClear
          placeholder="Equipment type"
        >
          <Select.Option value="ALL">ALL</Select.Option>
          {equipmentTypes.map((type) => (
            <Select.Option key={type} value={type}>{type}</Select.Option>
          ))}
        </Select>

        <div style={filterStyles.filterDivider} />

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
          <button
            onClick={handleApplyFilters}
            disabled={loading}
            style={{
              padding: '5px 14px',
              background: loading ? '#a0aec0' : '#2c5282',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              transition: 'all 0.2s',
            }}
          >
            <SearchOutlined style={{ fontSize: '11px' }} />
            {loading ? 'Loading...' : 'Apply'}
          </button>
          <button
            onClick={handleReset}
            style={{
              padding: '5px 12px',
              background: '#ffffff',
              color: '#4a5568',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              transition: 'all 0.2s',
            }}
          >
            <ReloadOutlined style={{ fontSize: '11px' }} />
            Reset
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {overallData && (
        <Row gutter={16} style={{ marginBottom: '12px' }}>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Total Physical Entries"
                value={overallData.total_entries}
                prefix={<CheckCircleOutlined style={{ color: '#1890ff' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Overall Digital Logging Rate"
                value={overallData.digitalLoggingRate}
                suffix="%"
                prefix={<PercentageOutlined style={{ color: '#52c41a' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Overall Accuracy"
                value={overallData.accuracy}
                suffix="%"
                valueStyle={{
                  color: parseFloat(overallData.accuracy) >= 90 ? '#52c41a'
                    : parseFloat(overallData.accuracy) >= 70 ? '#faad14' : '#ff4d4f',
                }}
                prefix={<WarningOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Calculation Logic Section */}
      <Collapse
        style={{ marginBottom: '12px' }}
        expandIconPosition="end"
        items={[
          {
            key: '1',
            label: (
              <span>
                <CalculatorOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                <Text strong>How Metrics Are Calculated</Text>
              </span>
            ),
            children: (
              <div>
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Title level={5} style={{ marginBottom: '12px' }}>📊 Percentage Calculations</Title>
                    <div style={formulaBlockStyle}>
                      {[
                        ['Correct %', '= (Correct Matches / Total Physical Entries) × 100'],
                        ['Wrong %', '= (Wrong Timings / Total Physical Entries) × 100'],
                        ['Missing %', '= (Missing Entries / Total Physical Entries) × 100'],
                        ['Digital Logging Rate %', '= (Digitally Logged / Total Physical Entries) × 100'],
                        ['Accuracy %', '= (Correct Matches / Total Physical Entries) × 100'],
                      ].map(([label, formula], i, arr) => (
                        <div key={label}>
                          <div style={{ marginBottom: '8px' }}>
                            <Text type="secondary">{label}</Text><br />
                            <Text code style={formulaStyle}>{formula}</Text>
                          </div>
                          {i < arr.length - 1 && <Divider style={{ margin: '8px 0' }} />}
                        </div>
                      ))}
                    </div>
                  </Col>
                  <Col xs={24} md={12}>
                    <Title level={5} style={{ marginBottom: '12px' }}>🔗 Data Integrity Relationships</Title>
                    <div style={formulaBlockStyle}>
                      <div style={{ marginBottom: '12px' }}>
                        <Text type="secondary">Key Relationship:</Text><br />
                        <Text code style={formulaStyle}>Digitally Logged + Missing Entries = Total Physical Entries</Text>
                      </div>
                      <Divider style={{ margin: '8px 0' }} />
                      <div style={{ marginBottom: '12px' }}>
                        <Text type="secondary">Logging Breakdown:</Text><br />
                        <Text code style={formulaStyle}>Correct Matches + Wrong Timings = Digitally Logged</Text>
                      </div>
                      <Divider style={{ margin: '8px 0' }} />
                      <div>
                        <Text strong style={{ color: '#1890ff' }}>Therefore:</Text>
                        <div style={{ marginTop: '8px', paddingLeft: '12px', borderLeft: '3px solid #1890ff' }}>
                          <Text code style={formulaStyle}>Digitally Logged = Correct Matches + Wrong Timings</Text>
                          <br /><br />
                          <Text code style={formulaStyle}>Total Physical Entries = Digitally Logged + Missing Entries</Text>
                        </div>
                      </div>
                    </div>
                    <div style={{ backgroundColor: '#e6f7ff', padding: '12px', borderRadius: '6px', border: '1px solid #91d5ff', marginTop: '12px' }}>
                      <InfoCircleOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        <strong>Tip:</strong> Hover over column headers with <InfoCircleOutlined style={{ color: '#1890ff' }} /> icon to see individual metric calculations.
                      </Text>
                    </div>
                  </Col>
                </Row>
              </div>
            ),
          },
        ]}
      />

      {/* Results Table */}
      <div style={{
        background: 'white', borderRadius: '8px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden', border: '1px solid #e2e8f0'
      }}>
        <div style={{
          padding: '10px 14px', borderBottom: '1px solid #e2e8f0',
          background: '#f7fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <h2 style={{ fontSize: '13px', fontWeight: '700', color: '#1a202c', margin: 0 }}>
            Reconciliation Summary
          </h2>
        </div>
        <Spin spinning={loading}>
          {tableData.length > 0 ? (
            <Table
              columns={columns}
              dataSource={tableData}
              pagination={false}
              scroll={{ x: 1300 }}
              rowClassName={(record) => record.equipment_type === 'Overall' ? 'overall-row' : ''}
              bordered
              size="middle"
            />
          ) : (
            <Empty
              style={{ padding: '40px' }}
              description={loading ? 'Loading data...' : 'No data available. Please apply filters to view results.'}
            />
          )}
        </Spin>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
        .overall-row { font-weight: bold; background-color: #fafafa; }
        .overall-row:hover > td { background-color: #f0f0f0 !important; }
      `}</style>
    </div>
  );
};

export default EquipmentLogAccuracyDashboard;